import React from "react";
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  VStack,
  Divider,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import FaqSection from "../FaqSection";
import { medicalWebPageSchema, serviceSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

// Rendered by <FaqSection> and emitted as FAQPage JSON-LD by <Seo>.
// Keep the two in sync by only editing this array.
const FAQS = [
  {
    q: "How long do dentures last?",
    a: "Dentures typically last 5 to 10 years, depending on the quality of care they receive and the wear and tear on them.",
  },
  {
    q: "How do I know if I need dentures?",
    a: "If you’re missing multiple teeth or experience pain or difficulty chewing and speaking, dentures may be a solution for you. A consultation with your dentist will help you determine the best option.",
  },
  {
    q: "Can dentures be repaired if broken?",
    a: "Yes, most dentures can be repaired by a dental professional if they break or become damaged.",
  },
  {
    q: "Do dentures affect taste?",
    a: "Some people experience a slight change in taste when they first get dentures, but this usually improves with time.",
  },
  {
    q: "Are dentures covered by insurance?",
    a: "Many dental insurance plans cover at least part of the cost of dentures, but it varies depending on your plan. It’s best to check with your insurance provider.",
  },
  {
    q: "Can I eat normally with dentures?",
    a: "Yes, with practice, most people can eat a variety of foods with dentures. It may take some time to adjust to chewing, so start with soft foods and gradually work your way up.",
  },
  {
    q: "Can I wear dentures while sleeping?",
    a: "It’s recommended to remove your dentures at night to give your gums a rest and prevent any irritation.",
  },
  {
    q: "Can dentures cause bad breath?",
    a: "Yes, poor denture hygiene can lead to bad breath. Make sure to clean your dentures regularly and maintain good oral hygiene.",
  },
  {
    q: "Will dentures affect my speech?",
    a: "It is common to experience some changes in speech when wearing new dentures. It may take a few days or weeks to adjust, but with practice, most people find that their speech improves as they get used to their dentures.",
  },
  {
    q: "Can I use my old dentures if they no longer fit?",
    a: "It’s not advisable to continue using ill-fitting dentures. Over time, your mouth may change, and old dentures can cause discomfort or even oral health problems. Visit your dentist for a new fitting.",
  },
];

const PAGE_PATH = "/dentures";
const PAGE_URL = absoluteUrl(PAGE_PATH);

const Dentures = () => {
  return (
    <Box bg="#fceee5">
      <Seo
        path={PAGE_PATH}
        title="Dentures in Jaipur | Comfortable, Natural-Looking Fit"
        description="Custom-fit complete and partial dentures in Vaishali Nagar, Jaipur. Comfortable, natural-looking replacements for missing teeth at Qualiteeth Dental Care."
        type="article"
        faqs={FAQS}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Dentures", path: PAGE_PATH },
        ]}
        schemas={[
          medicalWebPageSchema({
            url: PAGE_URL,
            title: "Dentures in Jaipur | Comfortable, Natural-Looking Fit",
            description: "Custom-fit complete and partial dentures in Vaishali Nagar, Jaipur. Comfortable, natural-looking replacements for missing teeth at Qualiteeth Dental Care.",
            procedureName: "Denture Fitting",
          }),
          serviceSchema({
            url: PAGE_URL,
            name: "Denture Fitting",
            description: "Custom-fit complete and partial dentures in Vaishali Nagar, Jaipur. Comfortable, natural-looking replacements for missing teeth at Qualiteeth Dental Care.",
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          Everything You Need to Know About Dentures
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="normal" mb={4}>
          Dentures are removable prosthetic teeth that replace some or all of a patient's natural teeth. Complete dentures replace a full arch, while partial dentures fill the gaps left by a few missing teeth. They restore chewing, speech and facial support, and are custom-made from impressions of the mouth.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          By Dr. Aishwarya Sarda, MDS — Conservative Dentistry & Endodontics
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What Are Dentures?
            </Heading>
            <Text mt={2} fontWeight="light">
              Dentures are removable dental appliances designed to replace
              missing teeth and restore the function and appearance of your
              smile. They are custom-made to fit the shape of your mouth and are
              typically made from materials like acrylic, porcelain, or a
              combination of both. Dentures are a great solution for individuals
              who have lost teeth due to age, injury, or dental disease. They
              can be partial or full, depending on the number of teeth that need
              replacement.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Types of Dentures
            </Heading>
            <Text mt={2} fontWeight="light">
              There are several types of dentures, each suited to different
              needs:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Full Dentures:</strong> These are used when all the
                  teeth in either the upper or lower jaw are missing. Full
                  dentures replace the entire arch of teeth and are supported by
                  the gums.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Partial Dentures:</strong> These are used when some
                  natural teeth remain. Partial dentures fill in the gaps where
                  teeth are missing and are attached to nearby natural teeth
                  with clasps or precision attachments.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Immediate Dentures:</strong> These are placed
                  immediately after tooth extraction, so you don’t have to go
                  without teeth during the healing process. However, they may
                  need adjustments as your mouth heals.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Implant-Supported Dentures:</strong> These dentures
                  are attached to dental implants that are surgically placed in
                  the jawbone. Implant-supported dentures provide a more secure
                  fit and are ideal for individuals who don’t have enough
                  natural teeth remaining.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Snap-in Dentures:</strong> These are similar to
                  implant-supported dentures, but they snap into a metal frame
                  or attachment placed in your mouth. These dentures are
                  removable and provide a stable fit.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dentures Procedure
            </Heading>
            <Text mt={2} fontWeight="light">
              The process of getting dentures typically involves several steps:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Initial Consultation:</strong> During the first visit,
                  your dentist will evaluate your oral health and discuss your
                  options for dentures. If teeth need to be extracted, they may
                  also recommend removing any remaining teeth before moving
                  forward with dentures.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Impressions and Measurements:</strong> Once your teeth
                  are extracted (if necessary), your dentist will take detailed
                  impressions of your gums and mouth. These impressions are used
                  to create custom dentures that fit comfortably and securely.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Fitting and Adjustments:</strong> After your dentures
                  are made, you will return to the dentist for a fitting. Your
                  dentist will check the fit and make any necessary adjustments
                  to ensure they’re comfortable and function properly.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Follow-Up Visits:</strong> After receiving your
                  dentures, you will need to return to your dentist for
                  follow-up visits to ensure the fit is still correct and to
                  address any issues, such as soreness or difficulty chewing.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Benefits of Dentures
            </Heading>
            <Text mt={2} fontWeight="light">
              Dentures offer a variety of benefits for individuals with missing
              teeth, including:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Improved Appearance:</strong> Dentures restore the
                natural appearance of your smile, making you look younger and
                more confident.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Better Functionality:</strong> Dentures allow you to
                chew and speak more comfortably, improving your ability to enjoy
                a variety of foods and communicate clearly.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Preservation of Facial Structure:</strong> By filling in
                gaps left by missing teeth, dentures help maintain the shape of
                your face, preventing sagging or a sunken appearance that can
                result from tooth loss.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Affordable Solution:</strong> Compared to other tooth
                replacement options like dental implants, dentures are often
                more affordable.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Non-Invasive:</strong> Dentures are removable and
                non-invasive, meaning there’s no need for surgery or lengthy
                recovery periods.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Denture Alternatives
            </Heading>
            <Text mt={2} fontWeight="light">
              If dentures aren’t the right option for you, there are a few
              alternatives that may be considered:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Dental Implants:</strong> Implants are a permanent
                solution that involves surgically placing a titanium post into
                the jawbone, which serves as a root for artificial teeth. They
                are secure and look and function like natural teeth.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Bridges:</strong> A dental bridge is used to replace one
                or more missing teeth by attaching an artificial tooth to
                adjacent natural teeth. Unlike dentures, bridges are fixed in
                place and cannot be removed.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Flexible Partial Dentures:</strong> These are made from
                a flexible, durable material that allows for a comfortable fit.
                Unlike traditional partial dentures, they don’t require metal
                clasps and may be less noticeable.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Overdentures:</strong> These are dentures that fit over
                remaining natural teeth or implants, providing additional
                support and stability.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dentures Care
            </Heading>
            <Text mt={2} fontWeight="light">
              Proper care is essential for maintaining the longevity and comfort
              of your dentures:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Cleaning:</strong> Brush your dentures daily with a
                soft-bristled brush and a non-abrasive cleaner to remove food
                particles and plaque. Avoid using regular toothpaste, as it can
                scratch the surface.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Soaking:</strong> When you’re not wearing your dentures,
                soak them in a denture solution or water to prevent them from
                drying out and losing their shape.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Avoid Hot Water:</strong> Never soak your dentures in
                hot water, as it can cause them to warp.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Check for Fit:</strong> Regularly check your dentures
                for any signs of loosening or discomfort. If your dentures
                become loose or cause irritation, visit your dentist for
                adjustments.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Oral Hygiene:</strong> Even if you wear dentures, it’s
                important to maintain good oral hygiene. Brush your gums,
                tongue, and any remaining natural teeth daily to prevent plaque
                buildup and gum disease.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dentures Do's and Don’ts
            </Heading>
            <Text mt={2} fontWeight="bold">
              Do’s:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Do clean your dentures regularly</strong> to maintain
                their appearance and prevent plaque buildup.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Do visit your dentist</strong> regularly for checkups
                and adjustments to ensure a good fit and proper function.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Do take your dentures out at night</strong> to give your
                gums a chance to rest and recover.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="bold">
              Don’ts:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Don’t wear your dentures 24/7.</strong> It’s important
                to give your gums time to heal and prevent irritation by
                removing your dentures at night.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Don’t use regular toothpaste</strong> to clean your
                dentures, as it can cause scratches. function.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Don’t drop your dentures.</strong> They can crack or
                break if dropped, so always handle them with care.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dentures Myths and Facts
            </Heading>
            <Text mt={2} fontWeight="light">
              <strong>Myth 1:</strong> Dentures are only for older people.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Dentures can be an option for anyone who
                has lost teeth, regardless of age. They provide a solution for
                individuals with missing teeth due to injury, disease, or age.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 2:</strong> Dentures are uncomfortable and don’t fit
              properly.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Modern dentures are designed to fit
                comfortably and securely. If they don’t fit well, adjustments
                can be made by your dentist.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 3:</strong> Dentures require a lot of maintenance.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Dentures do require regular cleaning and
                maintenance, but with proper care, they can last for many years.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 4:</strong> Dentures don’t look natural.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Advances in dental technology have made
                dentures look and feel more natural than ever. Your dentist will
                ensure that your dentures match the color and shape of your
                natural teeth.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 5:</strong> Dentures affect speech.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> While it may take a little time to
                adjust, most people quickly adapt to speaking with dentures, and
                they often find their speech improves after a short period of
                use.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <FaqSection items={FAQS} />
        </VStack>
      </Box>
    </Box>
  );
};

export default Dentures;
